import type { UseQueryResult } from '@tanstack/react-query';
import type { Api, BookingResponse, GuestProfileResponse } from '@/shared/api';
import { problemText, useApiProblem } from '@/shared/api';
import { BookingContactSummary } from './BookingContactSummary';
import { ConsentTrailCollapsible } from './ConsentTrailCollapsible';
import { DocumentsCard } from './DocumentsCard';
import { GuestProfileCard } from './GuestProfileCard';
import { OnboardingMailsCard } from './OnboardingMailsCard';
import { SectionCard } from './SectionCard';
import type { GuestState } from './useBookingWorkspace';

interface Props {
  api: Api;
  booking: BookingResponse;
  guest: UseQueryResult<GuestProfileResponse>;
  guestState: GuestState;
  guestName: string;
}

/** Reiter Gast (Board „BuchungGast“): Kontakt, Onboarding und Links, Gastprofil, Dokumente, Einwilligungen. */
export function BookingGuestTab({ api, booking, guest, guestState, guestName }: Props) {
  const profile = guest.data;
  return (
    <div className="flex flex-col gap-4">
      <SectionCard icon="Users" title="Kontakt">
        <BookingContactSummary
          guestName={guestName}
          phone={profile?.phone ?? booking.guestPhone}
          email={profile?.email}
        />
      </SectionCard>
      <OnboardingMailsCard
        bookingId={booking.id}
        propertyId={booking.propertyId}
        departed={booking.stayPhase === 'Completed'}
      />
      {profile ? (
        <>
          <GuestProfileCard guest={profile} />
          <DocumentsCard api={api} guest={profile} />
          <ConsentTrailCollapsible guestId={profile.id} />
        </>
      ) : (
        <GuestProfileState state={guestState} guest={guest} />
      )}
    </div>
  );
}

const STATE_TEXT: Record<Exclude<GuestState, 'ready' | 'error'>, [string, string]> = {
  none: [
    'Gastprofil & Rechnungsdaten · noch nicht abgegeben',
    'Persönliche Daten, Mitreisende und Rechnungsdaten erscheinen hier, sobald der Gast den Check-in abschließt.',
  ],
  hidden: ['Gastprofil', 'Die Gastdaten sind für Ihr Konto nicht freigegeben.'],
  loading: ['Gastprofil', 'Gastprofil wird geladen …'],
};

/** Ohne Profil: noch keins, verborgen, lädt, oder Fehler mit Grund – nie endlos „Lade Gast-Daten…“. */
function GuestProfileState({
  state,
  guest,
}: {
  state: GuestState;
  guest: UseQueryResult<GuestProfileResponse>;
}) {
  const problem = useApiProblem(guest.error);
  const [title, text] =
    state === 'error' || state === 'ready'
      ? ['Gastprofil', problemText(problem, 'Das Gastprofil konnte nicht geladen werden.')]
      : STATE_TEXT[state];
  return (
    <SectionCard icon="Users" title={title}>
      <p role={state === 'error' ? 'alert' : undefined} className="text-ink-soft text-sm">
        {text}
      </p>
      {state === 'error' && (
        <button
          type="button"
          onClick={() => void guest.refetch()}
          className="text-forest min-h-11 self-start text-sm font-bold"
        >
          Erneut laden
        </button>
      )}
    </SectionCard>
  );
}
